import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControlLabel,
  Switch,
  styled,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Alert,
} from "@mui/material";
import TestCaseSelector from "../../components-new/common/TestCaseSelector";
import { useTestCaseResultsQuery } from "../../useQuery";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    minWidth: "400px",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  color: "#1D4ED8",
  fontSize: "24px",
  fontWeight: 600,
  padding: theme.spacing(2, 3),
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2, 3),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: "8px 24px",
  borderRadius: "8px",
  textTransform: "capitalize",
  fontSize: "16px",
  fontWeight: 500,
  minWidth: "160px",
  height: "40px",
  backgroundColor: "#ff5336",
  color: "white",
  "&:hover": {
    backgroundColor: "#e94a2f",
  },
  "&.Mui-disabled": {
    backgroundColor: "#ffcdd2",
    color: "white",
  },
  "&.goto": {
    backgroundColor: "#2196F3",
    "&:hover": {
      backgroundColor: "#1976D2",
    },
  },
  "&.runtill": {
    backgroundColor: "#FF9800",
    "&:hover": {
      backgroundColor: "#F57C00",
    },
  },
  "&.resumeafter": {
    backgroundColor: "#9C27B0",
    "&:hover": {
      backgroundColor: "#7B1FA2",
    },
  },
}));

const DebugConfigModal = ({
  open,
  onClose,
  onExecute,
  testSteps = [],
  onDebugAction,
  initialConfig = { isDebugMode: false, debugStepNumber: null, debugMode: "run" },
  isExecutionReports = false,
  debugMessage = "",
  onStopExecution,
}) => {
  const [config, setConfig] = useState(initialConfig);
  const [stepNumberError, setStepNumberError] = useState("");
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [selectedTestCaseSteps, setSelectedTestCaseSteps] = useState([]);


  const { data: selectedTestCaseData } = useTestCaseResultsQuery(selectedTestCase?.idtest_case, {
    enabled: !!selectedTestCase?.idtest_case,
  });


  useEffect(() => {
    if (selectedTestCaseData?.data) {
      setSelectedTestCaseSteps(selectedTestCaseData.data);
    }
  }, [selectedTestCaseData]);

  const handleDebugModeChange = (event) => {
    setConfig({
      ...config,
      isDebugMode: event.target.checked,
      debugStepNumber: event.target.checked ? config.debugStepNumber : null,
    });
  };

  const handleDebugTypeChange = (event) => {
    setConfig({
      ...config,
      debugMode: event.target.value,
      debugStepNumber: null,
    });
    setStepNumberError("");
  };

  const handleStepSelect = (event) => {
    const selectedStep = selectedTestCaseSteps.find((step) => step.idtest_step === event.target.value);
    setConfig({
      ...config,
      debugStepNumber: selectedStep?.Step_Number || null,
      selectedStepId: event.target.value,
    });
    setStepNumberError("");
  };

  const handleExecute = () => {
    if (config.isDebugMode && !config.selectedStepId && stepNumberError === "") {
      setStepNumberError("Please select a step or disable debug mode");
      return;
    }
    onExecute({
      ...config,
      option:
        config.debugMode === "goto"
          ? "gotostep"
          : config.debugMode === "resumeafter"
            ? "resumeafter"
            : config.debugMode === "runtill"
              ? "runtill"
              : "run",
      idtest_step: config.selectedStepId,
      // selectedTestCaseId: selectedTestCase?.idtest_case,
    });
    onClose();
  };

  const handleDebugAction = (action) => {
    if (!config.selectedStepId && action !== "resume") {
      setStepNumberError("Please select a step first");
      return;
    }

    onDebugAction({
      option: action,
      idtest_step: config.selectedStepId,
      // selectedTestCaseId: selectedTestCase?.idtest_case,
    });
    onClose();
  };

  const getStepNumberLabel = () => {
    switch (config.debugMode) {
      case "goto":
        return "Select step to go to";
      case "resumeafter":
        return "Select step to resume after";
      case "runtill":
        return "Select step to run till";
      default:
        return "Select step";
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={false}
      disableBackdropClick={false}
    >
      <StyledDialogTitle>
        {isExecutionReports ? "Debug Action Required" : "Debug Configuration"}
      </StyledDialogTitle>
      <StyledDialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          {isExecutionReports && debugMessage && (
            <Typography variant="body1" gutterBottom>
              {debugMessage}
            </Typography>
          )}

          {isExecutionReports && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You can close this modal to review test steps, but you'll need to select a debug action to continue execution.
            </Alert>
          )}

          {!isExecutionReports && (
            <FormControlLabel
              control={
                <Switch
                  checked={config.isDebugMode}
                  onChange={handleDebugModeChange}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1" fontWeight={500}>
                  Enable Debug Mode
                </Typography>
              }
            />
          )}

          {(config.isDebugMode || isExecutionReports) && (
            <>
              {!isExecutionReports && (
                <FormControl component="fieldset">
                  <FormLabel component="legend">Debug Mode Type</FormLabel>
                  <RadioGroup value={config.debugMode} onChange={handleDebugTypeChange}>
                    <FormControlLabel value="goto" control={<Radio />} label="Go To Step" />
                    <FormControlLabel
                      value="resumeafter"
                      control={<Radio />}
                      label="Resume After Step"
                    />
                    <FormControlLabel value="runtill" control={<Radio />} label="Run Till Step" />
                  </RadioGroup>
                </FormControl>
              )}

              <Box>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Select Test Case
                </Typography>
                <TestCaseSelector 
                  onTestCaseSelect={setSelectedTestCase}
                  initialValues={{
                    productId: selectedTestCase?.idproducts || "",
                    moduleId: selectedTestCase?.idmodules || ""
                  }}
                />
              </Box>

              {selectedTestCase && (
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {isExecutionReports ? "Select Step" : getStepNumberLabel()}
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Step</InputLabel>
                    <Select
                      value={config.selectedStepId || ""}
                      onChange={handleStepSelect}
                      label="Step"
                      error={!!stepNumberError}
                    >
                      {selectedTestCaseSteps.map((step) => (
                        <MenuItem key={step.idtest_step} value={step.idtest_step}>
                          Step {step.Step_Number}: {step.Keyword} {step.Action} {step.SubAction}
                        </MenuItem>
                      ))}
                    </Select>
                    {stepNumberError && (
                      <Typography color="error" variant="caption">
                        {stepNumberError}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
              )}

              {isExecutionReports && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Choose an action to continue:
                </Typography>
              )}
            </>
          )}
        </Box>
      </StyledDialogContent>
      <DialogActions
        sx={{
          padding: 2,
          gap: 1,
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        {!isExecutionReports && (
          <>
            <StyledButton
              onClick={onClose}
              variant="outlined"
              sx={{
                color: "#ff5336",
                borderColor: "#ff5336",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "rgba(255, 83, 54, 0.04)",
                  borderColor: "#ff5336",
                },
              }}
            >
              Cancel
            </StyledButton>
            <StyledButton 
              onClick={handleExecute} 
              variant="contained"
              disabled={config.isDebugMode && !selectedTestCase}
            >
              Run
            </StyledButton>
          </>
        )}
        {isExecutionReports && (
          <>
            <StyledButton
              variant="outlined"
              onClick={onClose}
              sx={{
                color: "#9C27B0",
                borderColor: "#9C27B0",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "rgba(156, 39, 176, 0.04)",
                  borderColor: "#9C27B0",
                },
              }}
            >
              Close & Review
            </StyledButton>
            <StyledButton
              variant="contained"
              onClick={() => onStopExecution?.()}
              sx={{
                backgroundColor: "#ff5336",
                "&:hover": {
                  backgroundColor: "#e94a2f",
                },
              }}
            >
              Stop Execution
            </StyledButton>
            <StyledButton
              variant="contained"
              onClick={() => handleDebugAction("gotostep")}
              className="goto"
              disabled={!config.selectedStepId || !selectedTestCase}
            >
              Go To Step
            </StyledButton>
            <StyledButton
              variant="contained"
              onClick={() => handleDebugAction("runtill")}
              className="runtill"
              disabled={!config.selectedStepId || !selectedTestCase}
            >
              Run Till Step
            </StyledButton>
            <StyledButton
              variant="contained"
              onClick={() => handleDebugAction("resumeafter")}
              className="resumeafter"
              disabled={!config.selectedStepId || !selectedTestCase}
            >
              Resume After Step
            </StyledButton>
          </>
        )}
      </DialogActions>
    </StyledDialog>
  );
};

export default DebugConfigModal;
